import { Box } from '@mui/material';

/** viewBox coordinates for the destination pins (equirectangular-ish placement over the photo). */
const PINS: { label: string; x: number; y: number }[] = [
  { label: 'New York', x: 315, y: 150 },
  { label: 'London', x: 520, y: 108 },
  { label: 'Dubai', x: 618, y: 176 },
  { label: 'Singapore', x: 700, y: 250 },
  { label: 'Sydney', x: 806, y: 300 },
];

const ROUTES = [
  'M315,150 Q420,72 520,108',
  'M520,108 Q582,120 618,176',
  'M618,176 Q672,196 700,250',
  'M700,250 Q762,256 806,300',
];

/** Rough dotted-continent silhouettes (filled with a dot pattern) — a very subtle world map. */
const CONTINENTS = [
  'M120,110 Q160,70 232,90 Q300,102 300,150 Q292,200 240,212 Q168,230 140,190 Q108,150 120,110 Z',
  'M250,250 Q292,240 302,290 Q307,352 275,382 Q248,402 240,360 Q233,300 250,250 Z',
  'M478,86 Q520,72 546,100 Q552,132 514,142 Q484,146 474,120 Q470,98 478,86 Z',
  'M488,158 Q560,148 576,210 Q582,282 540,322 Q504,346 495,300 Q478,240 488,158 Z',
  'M558,78 Q680,52 802,88 Q862,110 840,162 Q780,202 690,190 Q600,186 570,150 Q553,110 558,78 Z',
  'M788,318 Q840,303 876,335 Q886,366 846,376 Q800,380 785,350 Q782,330 788,318 Z',
];

/**
 * The animated "sky" over the photograph: a subtle dotted world map, glowing flight routes, a glowing
 * plane and glass destination pins. Pure SVG so everything scales and stays pixel-aligned.
 */
export default function HeroSky({ reduce }: { reduce: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: { xs: '46%', md: '64%' },
        pointerEvents: 'none',
        opacity: { xs: 0.85, md: 1 },
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }}>
        <defs>
          <pattern id="worldDots" width="11" height="11" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.35" fill="#9DC3FF" />
          </pattern>
          <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="routeGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="50%" stopColor="#38BDF8" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
          <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="planeGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#38BDF8" floodOpacity="0.9" />
            <feDropShadow dx="0" dy="0" stdDeviation="9" floodColor="#2563EB" floodOpacity="0.55" />
          </filter>
        </defs>

        {/* Subtle world map */}
        <g opacity="0.12">
          {CONTINENTS.map((d) => (
            <path key={d} d={d} fill="url(#worldDots)" />
          ))}
        </g>

        {/* Flight routes */}
        <g fill="none" stroke="url(#routeGrad)" strokeWidth="1.5" strokeLinecap="round" filter="url(#softGlow)">
          {ROUTES.map((d) => (
            <path key={d} d={d} strokeDasharray="2 7" opacity="0.9">
              {!reduce && (
                <animate attributeName="stroke-dashoffset" from="0" to="-90" dur="3.2s" repeatCount="indefinite" />
              )}
            </path>
          ))}
        </g>

        {/* Destination pins — glowing dot + glass label */}
        {PINS.map((p) => {
          const w = p.label.length * 6.6 + 20;
          return (
            <g key={p.label}>
              <circle cx={p.x} cy={p.y} r="11" fill="url(#pinGlow)">
                {!reduce && <animate attributeName="r" values="8;13;8" dur="2.6s" repeatCount="indefinite" />}
              </circle>
              <circle cx={p.x} cy={p.y} r="3" fill="#E0F2FE" />
              <g transform={`translate(${p.x - w / 2}, ${p.y - 34})`}>
                <rect width={w} height="22" rx="7" fill="rgba(9,14,32,0.62)" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8" />
                <text x={w / 2} y="15" textAnchor="middle" fontSize="12.5" fontWeight="600" fill="#EAF2FF" style={{ fontFamily: 'inherit' }}>
                  {p.label}
                </text>
              </g>
            </g>
          );
        })}

        {/* Glowing contrail + plane */}
        <path d="M380,478 Q490,424 576,330" fill="none" stroke="#7DD3FC" strokeWidth="2" strokeLinecap="round" opacity="0.5" filter="url(#softGlow)" strokeDasharray="1 9">
          {!reduce && <animate attributeName="stroke-dashoffset" from="0" to="-100" dur="2.4s" repeatCount="indefinite" />}
        </path>
        <g transform="translate(592, 318) rotate(-32) scale(2.05)" filter="url(#planeGlow)">
          <path
            d="M30,0 L7,-3 L-3,-13 L-9,-13 L-2,-3 L-15,-3 L-20,-9 L-25,-9 L-21,-2 C-22,-0.8 -22,0.8 -21,2 L-25,9 L-20,9 L-15,3 L-2,3 L-9,13 L-3,13 L7,3 Z"
            fill="#F8FBFF"
          />
          {!reduce && (
            <animateTransform attributeName="transform" type="translate" values="0,0; 0,-4; 0,0" dur="5s" repeatCount="indefinite" additive="sum" />
          )}
        </g>
      </svg>
    </Box>
  );
}
